import { Autocomplete, Box, Button, FormControl, FormLabel, Grid, MenuItem, Modal, Select, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import get from "lodash/get";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import ProtectedPermissionView from "../../components/ProtectedPermissionView";
import useNotification from "../../hooks/useNotification";
import divisionService from "../../services/division";
import employeeService from "../../services/employee";
import mysteryShopperService from "../../services/mysteryShopper";
import queryDefault from "../../utils/queryDefault";

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Sorry, data is not found
    </Typography>
  </Box>
);

const formatDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const imageModalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 3,
  borderRadius: 2,
  maxHeight: "90vh",
  overflowY: "auto" as const,
};

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg", "avif"];

const isImageFile = (url: string) => {
  const extension = url.split("?")[0].split(".").pop()?.toLowerCase();
  return !!extension && IMAGE_EXTENSIONS.includes(extension);
};

interface ImagePreviewData {
  title: string;
  images: string[];
}

interface DivisionSummary {
  division_name: string;
  total: number;
}

interface CategorySummary {
  total: number;
  divisions: DivisionSummary[];
}

interface MysteryShopperChartSummary {
  average_final_score: number;
  below_average: CategorySummary;
  same_or_more_than_average: CategorySummary;
}

interface DivisionOption {
  id: number;
  name?: string;
  division_name?: string;
}

type EditFormValues = {
  division_id: number | null;
  penawaran_store: string;
  crew_menyapa: string;
  penyambutan: string;
  bantuan: string;
  closing: string;
  menawarkan_loyalty_event: string;
  menawarkan_additional_print: string;
  musik: string;
  error: string;
  kepuasan_keseluruhan: string;
  kembali_lagi: string;
  rekomendasi: string;
  kerapihan_crew: number | string;
  keramahan: number | string;
  komunikasi: number | string;
  responsiveness: number | string;
  fokus_customer: number | string;
  attitude: number | string;
  penjelasan: number | string;
  kejelasan_arahan: number | string;
  pengetahuan_paket_harga: number | string;
  menjelaskan_fitur: number | string;
  kejelasan_informasi: number | string;
  kebersihan_lantai: number | string;
  kebersihan_booth: number | string;
  kebersihan_properti: number | string;
  kenyamanan_tempat: number | string;
  mesin_normal: number | string;
  kamera_jelas: number | string;
  printer_hasil: number | string;
  penjelasan_flow_antrian: number | string;
  waktu_tunggu: number | string;
  kerapihan_antrian: number | string;
  pengaturan_flow: number | string;
};

const boolFieldConfigs: { name: keyof EditFormValues; label: string }[] = [
  { name: "penawaran_store", label: "Penawaran Crew depan Store" },
  { name: "crew_menyapa", label: "Crew Menyapa" },
  { name: "penyambutan", label: "Penyambutan" },
  { name: "bantuan", label: "Bantuan" },
  { name: "closing", label: "Closing" },
  { name: "menawarkan_loyalty_event", label: "Menawarkan Loyalty/Event" },
  { name: "menawarkan_additional_print", label: "Menawarkan Additional Print" },
  { name: "musik", label: "Musik" },
  { name: "error", label: "Error/Lag" },
  { name: "kepuasan_keseluruhan", label: "Kepuasan Keseluruhan" },
  { name: "kembali_lagi", label: "Kembali Lagi" },
  { name: "rekomendasi", label: "Rekomendasi" },
];

const numFieldConfigs: { name: keyof EditFormValues; label: string }[] = [
  { name: "kerapihan_crew", label: "Kerapihan Crew" },
  { name: "keramahan", label: "Keramahan" },
  { name: "komunikasi", label: "Komunikasi" },
  { name: "responsiveness", label: "Responsiveness" },
  { name: "fokus_customer", label: "Fokus Customer" },
  { name: "attitude", label: "Attitude" },
  { name: "penjelasan", label: "Penjelasan" },
  { name: "kejelasan_arahan", label: "Kejelasan Arahan" },
  { name: "pengetahuan_paket_harga", label: "Pengetahuan Paket & Harga" },
  { name: "menjelaskan_fitur", label: "Menjelaskan Fitur" },
  { name: "kejelasan_informasi", label: "Kejelasan Informasi" },
  { name: "kebersihan_lantai", label: "Kebersihan Lantai" },
  { name: "kebersihan_booth", label: "Kebersihan Booth" },
  { name: "kebersihan_properti", label: "Kebersihan Properti" },
  { name: "kenyamanan_tempat", label: "Kenyamanan Tempat" },
  { name: "mesin_normal", label: "Mesin Normal" },
  { name: "kamera_jelas", label: "Kamera Jelas" },
  { name: "printer_hasil", label: "Printer Hasil" },
  { name: "penjelasan_flow_antrian", label: "Penjelasan Flow Antrian" },
  { name: "waktu_tunggu", label: "Waktu Tunggu" },
  { name: "kerapihan_antrian", label: "Kerapihan Antrian" },
  { name: "pengaturan_flow", label: "Pengaturan Flow" },
];

export default function ListMysteryShopperPage() {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const defaultStartDate = formatDateInputValue(firstDayOfMonth);
  const defaultEndDate = formatDateInputValue(today);
  const notification = useNotification();

  const [pageSize, setPageSize] = useState<number>(10);
  const [openImagePreview, setOpenImagePreview] = useState(false);
  const [openAssignEmployees, setOpenAssignEmployees] = useState(false);
  const [openEditDivision, setOpenEditDivision] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState<number | null>(null);
  const [imagePreviewData, setImagePreviewData] = useState<ImagePreviewData>({
    title: "",
    images: [],
  });
  const [selectedMysteryShopperId, setSelectedMysteryShopperId] = useState<number | null>(null);
  const [selectedDivisionId, setSelectedDivisionId] = useState<number | null>(null);
  const [selectedEditMysteryShopperId, setSelectedEditMysteryShopperId] = useState<number | null>(null);
  const [selectedEditDivisionId, setSelectedEditDivisionId] = useState<number | null>(null);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>([]);
  const [options, setOptions] = useState<{
    startDate: string;
    endDate: string;
    region?: string;
  }>({
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    region: "",
  });

  const filterForm = useForm({
    defaultValues: {
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      region: "",
    },
  });

  const editForm = useForm<EditFormValues>({
    defaultValues: {
      division_id: null,
      penawaran_store: "",
      crew_menyapa: "",
      penyambutan: "",
      bantuan: "",
      closing: "",
      menawarkan_loyalty_event: "",
      menawarkan_additional_print: "",
      musik: "",
      error: "",
      kepuasan_keseluruhan: "",
      kembali_lagi: "",
      rekomendasi: "",
      kerapihan_crew: "",
      keramahan: "",
      komunikasi: "",
      responsiveness: "",
      fokus_customer: "",
      attitude: "",
      penjelasan: "",
      kejelasan_arahan: "",
      pengetahuan_paket_harga: "",
      menjelaskan_fitur: "",
      kejelasan_informasi: "",
      kebersihan_lantai: "",
      kebersihan_booth: "",
      kebersihan_properti: "",
      kenyamanan_tempat: "",
      mesin_normal: "",
      kamera_jelas: "",
      printer_hasil: "",
      penjelasan_flow_antrian: "",
      waktu_tunggu: "",
      kerapihan_antrian: "",
      pengaturan_flow: "",
    },
  });

  const listMysteryShoppers = useQuery(
    ["mystery-shopper-list", options],
    ({ queryKey }) => mysteryShopperService.getMysteryShopperList(queryKey[1]),
    {
      ...queryDefault,
    }
  );

  const onFilter = (values: any) => {
    const normalizedRegion = String(values.region || "").trim();

    setOptions({
      startDate: values.startDate,
      endDate: values.endDate,
      region: normalizedRegion,
    });
  };

  const employeeOptionsQuery = useQuery(
    ["employee-options-by-division", selectedDivisionId],
    () => employeeService.getEmployee({ divisionid: selectedDivisionId }),
    {
      enabled: openAssignEmployees && !!selectedDivisionId,
      ...queryDefault,
    }
  );

  const divisionOptionsQuery = useQuery(
    "mystery-shopper-division-options",
    () => divisionService.getAllDivision({ limit: 1000, page: 1 }),
    {
      enabled: openEditDivision,
      ...queryDefault,
    }
  );

  const exportMysteryShopperMutation = useMutation(
    "export-mystery-shopper-list",
    mysteryShopperService.exportMysteryShopperList,
    {
      onSuccess: (response) => {
        const fileName = `mystery-shopper-${options.startDate}-to-${options.endDate}.xlsx`;
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        notification.onOpen({
          message: "Mystery shopper list downloaded successfully",
          type: "success",
          position: "top",
        });
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to download mystery shopper list",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const updateDivisionMutation = useMutation(
    "update-mystery-shopper-division",
    mysteryShopperService.updateMysteryShopperDivision,
    {
      onSuccess: () => {
        notification.onOpen({
          message: "Division updated successfully",
          type: "success",
          position: "top",
        });
        setOpenEditDivision(false);
        setSelectedEditMysteryShopperId(null);
        setSelectedEditDivisionId(null);
        listMysteryShoppers.refetch();
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to update division",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const editMutation = useMutation(
    "edit-mystery-shopper",
    mysteryShopperService.updateMysteryShopper,
    {
      onSuccess: () => {
        notification.onOpen({
          message: "Mystery shopper updated successfully",
          type: "success",
          position: "top",
        });
        setOpenEditDivision(false);
        setSelectedEditMysteryShopperId(null);
        setSelectedEditDivisionId(null);
        editForm.reset();
        listMysteryShoppers.refetch();
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to update mystery shopper",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const deleteMutation = useMutation(
    "delete-mystery-shopper",
    mysteryShopperService.deleteMysteryShopper,
    {
      onSuccess: () => {
        notification.onOpen({
          message: "Mystery shopper deleted successfully",
          type: "success",
          position: "top",
        });
        setOpenDeleteConfirm(false);
        setSelectedDeleteId(null);
        listMysteryShoppers.refetch();
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to delete mystery shopper",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const assignEmployeesMutation = useMutation(
    "assign-mystery-shopper-employees",
    mysteryShopperService.assignMysteryShopperEmployees,
    {
      onSuccess: () => {
        notification.onOpen({
          message: "Employees assigned successfully",
          type: "success",
          position: "top",
        });
        setOpenAssignEmployees(false);
        setSelectedMysteryShopperId(null);
        setSelectedDivisionId(null);
        setSelectedEmployeeIds([]);
        listMysteryShoppers.refetch();
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to assign employees",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const getEmployeeDisplayName = (employee: any) =>
    employee?.name || employee?.employee_name || employee?.username || `Employee #${employee?.id ?? "-"}` || employee;

  const openAssignEmployeesModal = (row: any) => {
    const existingEmployees = Array.isArray(row?.employees) ? row.employees : [];
    const mappedIds = existingEmployees
      .map((employee: any) => Number(employee?.id ?? employee))
      .filter((id: number) => !Number.isNaN(id));

    setSelectedMysteryShopperId(Number(row.id));
    setSelectedDivisionId(Number(row.division_id));
    setSelectedEmployeeIds(mappedIds);
    setOpenAssignEmployees(true);
  };

  const closeAssignEmployeesModal = () => {
    if (assignEmployeesMutation.isLoading) {
      return;
    }
    setOpenAssignEmployees(false);
    setSelectedMysteryShopperId(null);
    setSelectedDivisionId(null);
    setSelectedEmployeeIds([]);
  };

  const openDeleteConfirmModal = (row: any) => {
    setSelectedDeleteId(Number(row.id));
    setOpenDeleteConfirm(true);
  };

  const closeDeleteConfirmModal = () => {
    if (deleteMutation.isLoading) {
      return;
    }
    setOpenDeleteConfirm(false);
    setSelectedDeleteId(null);
  };

  const onConfirmDelete = () => {
    if (!selectedDeleteId) {
      return;
    }
    deleteMutation.mutate(selectedDeleteId);
  };

  const openEditDivisionModal = (row: any) => {
    setSelectedEditMysteryShopperId(Number(row.id));
    setSelectedEditDivisionId(row?.division_id ? Number(row.division_id) : null);

    const normalizeBool = (val: any): string => {
      if (val === null || val === undefined || val === "") return "";
      return val === true || val === 1 || val === "true" ? "true" : "false";
    };

    editForm.reset({
      division_id: row?.division_id ? Number(row.division_id) : null,
      penawaran_store: normalizeBool(row.penawaran_store),
      crew_menyapa: normalizeBool(row.crew_menyapa),
      penyambutan: normalizeBool(row.penyambutan),
      bantuan: normalizeBool(row.bantuan),
      closing: normalizeBool(row.closing),
      menawarkan_loyalty_event: normalizeBool(row.menawarkan_loyalty_event),
      menawarkan_additional_print: normalizeBool(row.menawarkan_additional_print),
      musik: normalizeBool(row.musik),
      error: normalizeBool(row.error),
      kepuasan_keseluruhan: normalizeBool(row.kepuasan_keseluruhan),
      kembali_lagi: normalizeBool(row.kembali_lagi),
      rekomendasi: normalizeBool(row.rekomendasi),
      kerapihan_crew: row.kerapihan_crew ?? "",
      keramahan: row.keramahan ?? "",
      komunikasi: row.komunikasi ?? "",
      responsiveness: row.responsiveness ?? "",
      fokus_customer: row.fokus_customer ?? "",
      attitude: row.attitude ?? "",
      penjelasan: row.penjelasan ?? "",
      kejelasan_arahan: row.kejelasan_arahan ?? "",
      pengetahuan_paket_harga: row.pengetahuan_paket_harga ?? "",
      menjelaskan_fitur: row.menjelaskan_fitur ?? "",
      kejelasan_informasi: row.kejelasan_informasi ?? "",
      kebersihan_lantai: row.kebersihan_lantai ?? "",
      kebersihan_booth: row.kebersihan_booth ?? "",
      kebersihan_properti: row.kebersihan_properti ?? "",
      kenyamanan_tempat: row.kenyamanan_tempat ?? "",
      mesin_normal: row.mesin_normal ?? "",
      kamera_jelas: row.kamera_jelas ?? "",
      printer_hasil: row.printer_hasil ?? "",
      penjelasan_flow_antrian: row.penjelasan_flow_antrian ?? "",
      waktu_tunggu: row.waktu_tunggu ?? "",
      kerapihan_antrian: row.kerapihan_antrian ?? "",
      pengaturan_flow: row.pengaturan_flow ?? "",
    });

    setOpenEditDivision(true);
  };

  const closeEditDivisionModal = () => {
    if (editMutation.isLoading) {
      return;
    }
    setOpenEditDivision(false);
    setSelectedEditMysteryShopperId(null);
    setSelectedEditDivisionId(null);
    editForm.reset();
  };

  const onSaveAssignEmployees = () => {
    if (!selectedMysteryShopperId) {
      return;
    }

    assignEmployeesMutation.mutate({
      mystery_shopper_id: selectedMysteryShopperId,
      employee_ids: selectedEmployeeIds,
    });
  };

  const onSaveEdit = (values: EditFormValues) => {
    if (!selectedEditMysteryShopperId) {
      return;
    }

    const payload: Record<string, any> = { id: selectedEditMysteryShopperId };

    if (values.division_id !== null && values.division_id !== undefined) {
      payload.division_id = values.division_id;
    }

    boolFieldConfigs.forEach(({ name }) => {
      const val = values[name];
      if (val === "true") payload[name] = true;
      else if (val === "false") payload[name] = false;
    });

    numFieldConfigs.forEach(({ name }) => {
      const val = values[name];
      if (val !== "" && val !== null && val !== undefined) {
        payload[name] = Number(val);
      }
    });

    editMutation.mutate(payload);
  };

  const openImagePreviewModal = (title: string, images: (string | null)[]) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    setImagePreviewData({
      title,
      images: images
        .filter((img): img is string => img !== null && img !== undefined)
        .map((img) => (img.startsWith("http") ? img : `${backendUrl}${img}`)),
    });
    setOpenImagePreview(true);
  };

  const getMediaUrl = (path: string) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    return path.startsWith("http") ? path : `${backendUrl}${path}`;
  };

  const rows = get(listMysteryShoppers, "data.data", []);
  const employeeOptions = get(employeeOptionsQuery, "data.data", []);
  const divisionOptionsRaw =
    get(divisionOptionsQuery, "data.data", []) || get(divisionOptionsQuery, "data", []);
  const divisionOptions = Array.isArray(divisionOptionsRaw)
    ? (divisionOptionsRaw as DivisionOption[])
    : [];
  const chartSummary = get(
    listMysteryShoppers,
    "data.chart_summary",
    null
  ) as MysteryShopperChartSummary | null;

  const belowAverageTotal = Number(get(chartSummary, "below_average.total", 0));
  const sameOrMoreTotal = Number(
    get(chartSummary, "same_or_more_than_average.total", 0)
  );
  const averageFinalScore = Number(get(chartSummary, "average_final_score", 0));
  const totalCompared = belowAverageTotal + sameOrMoreTotal;
  const belowAveragePercent = totalCompared > 0 ? (belowAverageTotal / totalCompared) * 100 : 0;
  const sameOrMorePercent = totalCompared > 0 ? (sameOrMoreTotal / totalCompared) * 100 : 0;

  const renderDivisionTooltip = (
    title: string,
    divisions: DivisionSummary[],
    categoryTotal: number
  ) => (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {title}
      </Typography>
      {divisions.length > 0 ? (
        divisions.map((division) => {
          const percent =
            categoryTotal > 0
              ? ((division.total / categoryTotal) * 100).toFixed(2)
              : "0.00";

          return (
            <Typography key={division.division_name} variant="caption" sx={{ display: "block" }}>
              {division.division_name}: {division.total} ({percent}%)
            </Typography>
          );
        })
      ) : (
        <Typography variant="caption">No division detail.</Typography>
      )}
    </Box>
  );

  const getDivisionOptionLabel = (option: DivisionOption) =>
    option?.name || option?.division_name || `Division #${option?.id ?? "-"}`;

  return (
    <MainLayout>
      <Helmet>
        <title>Mystery Shopper</title>
      </Helmet>

      <Breadcrumb
        label="Mystery Shopper"
        breadcrumbs={[{ label: "Mystery Shopper", href: "/app/mystery-shopper" }]}
      />

      <ProtectedPermissionPage acceptPermissions={[4, 5, 12]}>
        <Box sx={{ mt: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <FormLabel>Start Date</FormLabel>
              <Controller
                name="startDate"
                control={filterForm.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <FormLabel>End Date</FormLabel>
              <Controller
                name="endDate"
                control={filterForm.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <FormLabel>Region (optional)</FormLabel>
              <Controller
                name="region"
                control={filterForm.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    placeholder="e.g. jakarta"
                  />
                )}
              />
            </FormControl>

            <Box sx={{ alignSelf: "end" }}>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={filterForm.handleSubmit(onFilter)}>
                  Apply Filter
                </Button>
                <Button
                  variant="contained"
                  onClick={() => exportMysteryShopperMutation.mutate(options)}
                  disabled={exportMysteryShopperMutation.isLoading}
                >
                  Download
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Box>

        <Box sx={{ mt: 2 }}>
          {chartSummary && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: "background.paper",
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                Mystery Shopper Chart Summary
              </Typography>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    flex: 1,
                    p: 2,
                    borderRadius: 1,
                    bgcolor: "grey.100",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Average Final Score
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {Number(get(chartSummary, "average_final_score", 0)).toFixed(2)}
                  </Typography>
                </Box>

                <Tooltip
                  title={renderDivisionTooltip(
                    "Below Average Divisions",
                    get(chartSummary, "below_average.divisions", []),
                    belowAverageTotal
                  )}
                  arrow
                  placement="top"
                >
                  <Box
                    sx={{
                      flex: 1,
                      p: 2,
                      borderRadius: 1,
                      bgcolor: "error.lighter",
                      cursor: "help",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Below Average
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {belowAverageTotal}
                    </Typography>
                  </Box>
                </Tooltip>

                <Tooltip
                  title={renderDivisionTooltip(
                    "Same or Above Average Divisions",
                    get(chartSummary, "same_or_more_than_average.divisions", []),
                    sameOrMoreTotal
                  )}
                  arrow
                  placement="top"
                >
                  <Box
                    sx={{
                      flex: 1,
                      p: 2,
                      borderRadius: 1,
                      bgcolor: "success.lighter",
                      cursor: "help",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Same or Above Average
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {sameOrMoreTotal}
                    </Typography>
                  </Box>
                </Tooltip>
              </Stack>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Distribution
              </Typography>
              <Stack direction="row" sx={{ width: "100%", height: 18, borderRadius: 1, overflow: "hidden" }}>
                <Tooltip
                  title={renderDivisionTooltip(
                    `Below Average (${belowAverageTotal})`,
                    get(chartSummary, "below_average.divisions", []),
                    belowAverageTotal
                  )}
                  arrow
                >
                  <Box
                    sx={{
                      width: `${belowAveragePercent}%`,
                      minWidth: belowAverageTotal > 0 ? 24 : 0,
                      bgcolor: "error.main",
                      cursor: "help",
                    }}
                  />
                </Tooltip>

                <Tooltip
                  title={renderDivisionTooltip(
                    `Same or Above Average (${sameOrMoreTotal})`,
                    get(chartSummary, "same_or_more_than_average.divisions", []),
                    sameOrMoreTotal
                  )}
                  arrow
                >
                  <Box
                    sx={{
                      width: `${sameOrMorePercent}%`,
                      minWidth: sameOrMoreTotal > 0 ? 24 : 0,
                      bgcolor: "success.main",
                      cursor: "help",
                    }}
                  />
                </Tooltip>
              </Stack>
            </Box>
          )}

          <DataGrid
            autoHeight
            loading={listMysteryShoppers.isLoading}
            getRowId={(record) => get(record, "id")}
            rows={rows}
            rowCount={rows.length}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 20, 30]}
            pagination
            disableSelectionOnClick
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            getRowClassName={(params) => {
              if (!chartSummary || Number.isNaN(averageFinalScore)) {
                return "";
              }

              const finalScore = Number(params.row?.final_score);
              if (Number.isNaN(finalScore)) {
                return "";
              }

              return finalScore < averageFinalScore
                ? "row-below-average"
                : "row-same-or-above-average";
            }}
            components={{
              NoRowsOverlay: CustomNoRowOverlay,
            }}
            columns={[
              { field: "shopper_name", headerName: "Shopper Name", minWidth: 150, flex: 1 },
              { field: "shopper_email", headerName: "Shopper Email", minWidth: 180, flex: 1 },
              { field: "shopper_phone", headerName: "Shopper Phone", minWidth: 140 },
              {
                field: "visit_date", headerName: "Visit Date", minWidth: 120, renderCell: (params) => {
                  const dateValue = params.value;
                  return dateValue ? new Date(dateValue).toLocaleDateString() : "";
                }
              },
              { field: "visit_time", headerName: "Visit Time", minWidth: 120 },
              { field: "division_name", headerName: "Division Name", minWidth: 200 },
              { field: "kerapihan_crew", headerName: "Kerapihan Crew", minWidth: 130, type: "number" },
              { field: "catatan_grooming", headerName: "Catatan Grooming", minWidth: 200, flex: 1 },
              { field: "penawaran_store", headerName: "Penawaran Crew depan Store", minWidth: 190, renderCell: (params) => params.value ? "Ya" : "Tidak" },
              { field: "crew_menyapa", headerName: "Crew Menyapa", minWidth: 120, renderCell: (params) => params.value ? "Ya" : "Tidak" },
              { field: "keramahan", headerName: "Keramahan", minWidth: 100, type: "number" },
              { field: "komunikasi", headerName: "Komunikasi", minWidth: 100, type: "number" },
              { field: "responsiveness", headerName: "Responsiveness", minWidth: 130, type: "number" },
              { field: "fokus_customer", headerName: "Fokus Customer", minWidth: 130, type: "number" },
              { field: "attitude", headerName: "Attitude", minWidth: 100, type: "number" },
              { field: "catatan_behavior", headerName: "Catatan Behavior", minWidth: 200, flex: 1 },
              { field: "penyambutan", headerName: "Penyambutan", minWidth: 110, renderCell: (params) => params.value ? "Ya" : "Tidak" },
              { field: "penjelasan", headerName: "Penjelasan", minWidth: 100, type: "number" },
              { field: "bantuan", headerName: "Bantuan", minWidth: 90, renderCell: (params) => params.value ? "Ya" : "Tidak" },
              { field: "kejelasan_arahan", headerName: "Kejelasan Arahan", minWidth: 130, type: "number" },
              { field: "closing", headerName: "Closing", minWidth: 90, renderCell: (params) => params.value ? "Ya" : "Tidak" },
              { field: "catatan_service_flow", headerName: "Catatan Service Flow", minWidth: 170, flex: 1 },
              { field: "pengetahuan_paket_harga", headerName: "Pengetahuan Harga", minWidth: 150, type: "number" },
              { field: "menawarkan_loyalty_event", headerName: "Menawarkan Loyalty", minWidth: 170, type: "number" },
              { field: "menjelaskan_fitur", headerName: "Menjelaskan Fitur", minWidth: 120, type: "number" },
              { field: "menawarkan_additional_print", headerName: "Menawarkan Additional Print", minWidth: 170, renderCell: (params) => params.value ? "Ya" : "Tidak" },
              { field: "kejelasan_informasi", headerName: "Kejelasan Informasi", minWidth: 140, type: "number" },
              { field: "catatan_product_knowledge", headerName: "Catatan Product Knowledge", minWidth: 200, flex: 1 },
              { field: "kebersihan_lantai", headerName: "Kebersihan Lantai", minWidth: 120, type: "number" },
              { field: "kebersihan_booth", headerName: "Kebersihan Booth", minWidth: 120, type: "number" },
              { field: "kebersihan_properti", headerName: "Kebersihan Properti", minWidth: 120, type: "number" },
              { field: "kenyamanan_tempat", headerName: "Kenyamanan Tempat", minWidth: 120, type: "number" },
              { field: "musik", headerName: "Musik", minWidth: 90, renderCell: (params) => params.value ? "Ya" : "Tidak" },
              { field: "catatan_kondisi_store", headerName: "Catatan Kondisi Store", minWidth: 200, flex: 1 },
              { field: "mesin_normal", headerName: "Mesin Normal", minWidth: 110, type: "number" },
              { field: "kamera_jelas", headerName: "Kamera Jelas", minWidth: 110, type: "number" },
              { field: "printer_hasil", headerName: "Printer Hasil", minWidth: 110, type: "number" },
              { field: "error", headerName: "Error", minWidth: 90, renderCell: (params) => params.value ? "Ya" : "Tidak" },
              { field: "catatan_equipment", headerName: "Catatan Equipment", minWidth: 200, flex: 1 },
              { field: "penjelasan_flow_antrian", headerName: "Penjelasan Flow Antrian", minWidth: 150, type: "number" },
              { field: "waktu_tunggu", headerName: "Waktu Tunggu", minWidth: 110, type: "number" },
              { field: "kerapihan_antrian", headerName: "Kerapihan Antrian", minWidth: 120, type: "number" },
              { field: "pengaturan_flow", headerName: "Pengaturan Flow", minWidth: 120, type: "number" },
              { field: "catatan_antrian", headerName: "Catatan Antrian", minWidth: 200, flex: 1 },
              { field: "kepuasan_keseluruhan", headerName: "Kepuasan Keseluruhan", minWidth: 140, type: "number" },
              { field: "kembali_lagi", headerName: "Kembali Lagi", minWidth: 110, type: "number" },
              { field: "rekomendasi", headerName: "Rekomendasi", minWidth: 150, type: "number" },
              {
                field: "final_score", headerName: "Final Score", minWidth: 110, type: "number", renderCell: (params) => {
                  const num = Number(params.value);
                  return isNaN(num) ? 0 : Number(num.toFixed(2));
                }
              },
              { field: "red_flag", headerName: "Red Flag", minWidth: 120 },
              { field: "cerita_pengalaman", headerName: "Cerita Pengalaman", minWidth: 150, flex: 1 },
              {
                field: "foto_crew",
                headerName: "Foto Crew",
                minWidth: 120,
                sortable: false,
                renderCell: (params) => {
                  const row = params.row;
                  const fotoCrewImages = [
                    row.foto_crew_1,
                    row.foto_crew_2,
                    row.foto_crew_3,
                    row.foto_crew_4,
                    row.foto_crew_5,
                  ].filter((img) => img);

                  return fotoCrewImages.length > 0 ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openImagePreviewModal(`Foto Crew - ${row.shopper_name}`, fotoCrewImages)}
                    >
                      Preview ({fotoCrewImages.length})
                    </Button>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  );
                },
              },
              {
                field: "foto",
                headerName: "Foto Store",
                minWidth: 120,
                sortable: false,
                renderCell: (params) => {
                  const row = params.row;
                  const fotoImages = [
                    row.foto_1,
                    row.foto_2,
                    row.foto_3,
                    row.foto_4,
                    row.foto_5,
                    row.foto_6,
                    row.foto_7,
                    row.foto_8,
                    row.foto_9,
                    row.foto_10,
                  ].filter((img) => img);

                  return fotoImages.length > 0 ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openImagePreviewModal(`Foto Store - ${row.shopper_name}`, fotoImages)}
                    >
                      Preview ({fotoImages.length})
                    </Button>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  );
                },
              },
              {
                field: "voice_additional",
                headerName: "Voice Additional",
                minWidth: 150,
                sortable: false,
                renderCell: (params) => {
                  const path = params.value;
                  if (!path) {
                    return (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    );
                  }

                  return (
                    <Button
                      size="small"
                      variant="outlined"
                      component="a"
                      href={getMediaUrl(path)}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                    >
                      Download
                    </Button>
                  );
                },
              },
              {
                field: "video_jingle",
                headerName: "Video Jingle",
                minWidth: 130,
                sortable: false,
                renderCell: (params) => {
                  const path = params.value;
                  if (!path) {
                    return (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    );
                  }

                  return (
                    <Button
                      size="small"
                      variant="outlined"
                      component="a"
                      href={getMediaUrl(path)}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                    >
                      Download
                    </Button>
                  );
                },
              },
              {
                field: "createdtime", headerName: "Created Time", minWidth: 160, renderCell: (params) => {
                  const dateValue = params.value;
                  return dateValue ? new Date(dateValue).toLocaleString() : "";
                }
              },
              {
                field: "employees",
                headerName: "Employees",
                minWidth: 200
              },
              {
                field: "actions",
                headerName: "Action",
                minWidth: 230,
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                  <ProtectedPermissionView acceptPermissions={[4, 5]}>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openEditDivisionModal(params.row)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openAssignEmployeesModal(params.row)}
                      >
                        Assign
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => openDeleteConfirmModal(params.row)}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </ProtectedPermissionView>
                ),
              },
            ]}
            sx={{
              ".MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
              },
              ".MuiDataGrid-cell": {
                overflow: "hidden",
              },
              ".MuiDataGrid-cellContent": {
                width: "100%",
                display: "block",
                overflowX: "auto",
                overflowY: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "clip",
                scrollbarWidth: "thin",
              },
              ".MuiDataGrid-cellContent::-webkit-scrollbar": {
                height: 6,
              },
              ".MuiDataGrid-row.row-below-average .MuiDataGrid-cell": {
                backgroundColor: "rgba(211, 47, 47, 0.12)",
              },
              ".MuiDataGrid-row.row-same-or-above-average .MuiDataGrid-cell": {
                backgroundColor: "rgba(46, 125, 50, 0.12)",
              },
              ".MuiDataGrid-row.row-below-average:hover .MuiDataGrid-cell": {
                backgroundColor: "rgba(211, 47, 47, 0.18)",
              },
              ".MuiDataGrid-row.row-same-or-above-average:hover .MuiDataGrid-cell": {
                backgroundColor: "rgba(46, 125, 50, 0.18)",
              },
            }}
          />
        </Box>
      </ProtectedPermissionPage>

      <Modal open={openImagePreview} onClose={() => setOpenImagePreview(false)}>
        <Box sx={imageModalStyle}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            {imagePreviewData.title}
          </Typography>

          <Stack spacing={2}>
            {imagePreviewData.images.map((image, index) => (
              <Box key={index} sx={{ textAlign: "center" }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Image {index + 1}
                </Typography>
                {isImageFile(image) ? (
                  <img
                    src={image}
                    alt={`Preview ${index + 1}`}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "400px",
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                  />
                ) : (
                  <Button
                    variant="outlined"
                    component="a"
                    href={image}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                  >
                    Download File
                  </Button>
                )}
              </Box>
            ))}
          </Stack>

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button variant="contained" onClick={() => setOpenImagePreview(false)}>
              Close
            </Button>
          </Stack>
        </Box>
      </Modal>

      <Modal open={openAssignEmployees} onClose={closeAssignEmployeesModal}>
        <Box sx={{ ...imageModalStyle, width: "100%", maxWidth: 520 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            Assign Employees
          </Typography>

          <FormControl fullWidth size="small">
            <FormLabel>Select Employees</FormLabel>
            <Select
              multiple
              value={selectedEmployeeIds}
              onChange={(event) => {
                const value = event.target.value;
                const values = Array.isArray(value) ? value : String(value).split(",");
                setSelectedEmployeeIds(values.map((item) => Number(item)));
              }}
              renderValue={(selected) => {
                const ids = selected as number[];
                return employeeOptions
                  .filter((employee: any) => ids.includes(Number(employee.id)))
                  .map((employee: any) => getEmployeeDisplayName(employee))
                  .join(", ");
              }}
              sx={{ mt: 1 }}
            >
              {employeeOptionsQuery.isLoading && (
                <MenuItem disabled>Loading employees...</MenuItem>
              )}

              {!employeeOptionsQuery.isLoading && employeeOptions.length === 0 && (
                <MenuItem disabled>No employee found</MenuItem>
              )}

              {employeeOptions.map((employee: any) => (
                <MenuItem key={employee.id} value={employee.id}>
                  {getEmployeeDisplayName(employee)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              onClick={closeAssignEmployeesModal}
              disabled={assignEmployeesMutation.isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={onSaveAssignEmployees}
              disabled={assignEmployeesMutation.isLoading || !selectedMysteryShopperId}
            >
              Save
            </Button>
          </Stack>
        </Box>
      </Modal>

      <Modal open={openDeleteConfirm} onClose={closeDeleteConfirmModal}>
        <Box sx={{ ...imageModalStyle, width: "100%", maxWidth: 440 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
            Delete Mystery Shopper
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Are you sure you want to delete this mystery shopper record? This action cannot be undone.
          </Typography>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              variant="outlined"
              onClick={closeDeleteConfirmModal}
              disabled={deleteMutation.isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={onConfirmDelete}
              disabled={deleteMutation.isLoading}
            >
              Delete
            </Button>
          </Stack>
        </Box>
      </Modal>

      <Modal open={openEditDivision} onClose={closeEditDivisionModal}>
        <Box sx={{ ...imageModalStyle, width: "100%", maxWidth: 720 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            Edit Mystery Shopper
          </Typography>

          <Stack spacing={3}>
            <FormControl fullWidth size="small">
              <FormLabel>Division</FormLabel>
              <Controller
                name="division_id"
                control={editForm.control}
                render={({ field }) => (
                  <Autocomplete
                    sx={{ mt: 1 }}
                    options={divisionOptions}
                    loading={divisionOptionsQuery.isLoading}
                    value={
                      divisionOptions.find((option) => Number(option.id) === Number(field.value)) || null
                    }
                    getOptionLabel={getDivisionOptionLabel}
                    isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                    onChange={(_, option) => {
                      field.onChange(option ? Number(option.id) : null);
                      setSelectedEditDivisionId(option ? Number(option.id) : null);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} size="small" placeholder="Choose division" />
                    )}
                  />
                )}
              />
            </FormControl>

            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                Ya / Tidak
              </Typography>
              <Grid container spacing={2}>
                {boolFieldConfigs.map(({ name, label }) => (
                  <Grid item xs={12} sm={6} key={name}>
                    <FormControl fullWidth size="small">
                      <FormLabel>{label}</FormLabel>
                      <Controller
                        name={name}
                        control={editForm.control}
                        render={({ field }) => (
                          <Select {...field} size="small" sx={{ mt: 0.5 }} displayEmpty>
                            <MenuItem value="">-</MenuItem>
                            <MenuItem value="true">Ya</MenuItem>
                            <MenuItem value="false">Tidak</MenuItem>
                          </Select>
                        )}
                      />
                    </FormControl>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                Nilai (1–10)
              </Typography>
              <Grid container spacing={2}>
                {numFieldConfigs.map(({ name, label }) => (
                  <Grid item xs={12} sm={6} key={name}>
                    <FormControl fullWidth size="small">
                      <FormLabel>{label}</FormLabel>
                      <Controller
                        name={name}
                        control={editForm.control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            size="small"
                            type="number"
                            sx={{ mt: 0.5 }}
                            inputProps={{ min: 1, max: 10 }}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === "") {
                                field.onChange("");
                              } else {
                                field.onChange(Math.min(10, Math.max(1, Number(val))));
                              }
                            }}
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Stack>

          <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              onClick={closeEditDivisionModal}
              disabled={editMutation.isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={editForm.handleSubmit(onSaveEdit)}
              disabled={editMutation.isLoading || !selectedEditMysteryShopperId}
            >
              Save
            </Button>
          </Stack>
        </Box>
      </Modal>
    </MainLayout>
  );
}
